import { createContext, useContext } from 'react'
import { useBreakpoint } from '@/ui/kit'
import { GRID_COLUMNS, asPercentWidth, cn, resolveGutter } from './internal/utils'

const GutterContext = createContext([0, 0])

const JUSTIFY = {
  start: 'justify-start',
  end: 'justify-end',
  center: 'justify-center',
  'space-around': 'justify-around',
  'space-between': 'justify-between',
  'space-evenly': 'justify-evenly',
}

const ALIGN = {
  top: 'items-start',
  middle: 'items-center',
  bottom: 'items-end',
  stretch: 'items-stretch',
}

const RESPONSIVE_ORDER = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl']

/**
 * antd's 24-column Row. Gutters are applied as negative row margin plus per-Col
 * padding, exactly like antd, so nested rows keep aligning.
 */
const Row = ({
  gutter,
  justify,
  align,
  wrap = true,
  className,
  style,
  children,
  ...rest
}) => {
  const [horizontal, vertical] = resolveGutter(gutter)

  return (
    <GutterContext.Provider value={[horizontal, vertical]}>
      <div
        className={cn(
          'ui-row flex',
          wrap ? 'flex-wrap' : 'flex-nowrap',
          justify && JUSTIFY[justify],
          align && ALIGN[align],
          className
        )}
        style={{
          marginLeft: horizontal ? -horizontal / 2 : undefined,
          marginRight: horizontal ? -horizontal / 2 : undefined,
          rowGap: vertical || undefined,
          ...style,
        }}
        {...rest}
      >
        {children}
      </div>
    </GutterContext.Provider>
  )
}

/**
 * antd Col. `span`/`offset` plus the responsive props, which accept either a
 * number of columns or antd's `{ span, offset }` object form.
 */
const Col = ({
  span,
  offset,
  order,
  flex,
  push,
  pull,
  xs,
  sm,
  md,
  lg,
  xl,
  xxl,
  className,
  style,
  children,
  ...rest
}) => {
  const [horizontal] = useContext(GutterContext)
  const screens = useBreakpoint()

  // Largest matching breakpoint wins, matching antd's mobile-first cascade.
  const responsive = { xs, sm, md, lg, xl, xxl }
  let effectiveSpan = span
  let effectiveOffset = offset
  RESPONSIVE_ORDER.forEach((name) => {
    const value = responsive[name]
    if (value == null || !screens[name]) return
    if (typeof value === 'object') {
      if (value.span != null) effectiveSpan = value.span
      if (value.offset != null) effectiveOffset = value.offset
    } else {
      effectiveSpan = value
    }
  })

  const width = asPercentWidth(effectiveSpan)
  const isFullRow = effectiveSpan != null && Number(effectiveSpan) >= GRID_COLUMNS

  return (
    <div
      className={cn('ui-col', className)}
      style={{
        flex: flex ?? (width ? `0 0 ${width}` : undefined),
        maxWidth: width,
        width: isFullRow ? '100%' : undefined,
        marginLeft: asPercentWidth(effectiveOffset),
        order,
        left: asPercentWidth(push),
        right: asPercentWidth(pull),
        position: push || pull ? 'relative' : undefined,
        paddingLeft: horizontal ? horizontal / 2 : undefined,
        paddingRight: horizontal ? horizontal / 2 : undefined,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  )
}

const Grid = { useBreakpoint }

export { Col, Grid, Row, useBreakpoint }
export default Grid
